import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

const ActionableMasterDetail = () => {
  const { id } = useParams(); // actionable_number
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [file, setFile] = useState(null);

  const hiddenFields = ["id", "company_id", "evidence_file"];

  // Load master-detail
  useEffect(() => {
    const token = localStorage.getItem("accessToken");

    axios
      .get(`/api/actionables/master-detail/${id}/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      .then((res) => setData(res.data))
      .catch((err) => console.error("Master detail error:", err));
  }, [id]);

  const handleSubmitEvidence = async () => {
    if (!file) {
      alert("Please select a file first");
      return;
    }

    const formData = new FormData();
    formData.append("evidence", file);

    const token = localStorage.getItem("accessToken");

    await axios.post(`/api/actionables/master-upload/${id}/`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
        Authorization: `Bearer ${token}`,
      },
    });

    alert("Evidence submitted successfully!");
    navigate("/external-trigger");
  };

  const getStatusStyle = (status) => {
    if (!status) return {};
    const colorMap = {
      New: "#3b82f6",
      Pending: "#ef4444",
      Completed: "#10b981",
      "Completed After Deadline": "#f59e0b",
    };

    const color = colorMap[status] || "#6b7280";

    return {
      backgroundColor: `${color}15`,
      borderLeft: `4px solid ${color}`,
      color,
      fontWeight: "600",
      paddingLeft: "12px",
    };
  };

  if (!data) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingText}>Loading actionable details...</div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <button
          style={styles.backBtn}
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>
        <h1 style={styles.title}>Actionable Details (External Trigger)</h1>
      </div>

      {/* Summary Section */}
      <div style={styles.summaryContainer}>
        <div style={styles.summaryItem}>
          <span style={styles.emoji}>#️⃣</span>
          <div>
            <div style={styles.label}>Actionable Number</div>
            <div style={styles.value}>{data.actionable_number}</div>
          </div>
        </div>

        <div style={styles.summaryItem}>
          <span style={styles.emoji}>👤</span>
          <div>
            <div style={styles.label}>Owner</div>
            <div style={styles.value}>{data.username}</div>
          </div>
        </div>

        <div style={styles.summaryItem}>
          <span style={styles.emoji}>🏢</span>
          <div>
            <div style={styles.label}>Company</div>
            <div style={styles.value}>{data.company_name}</div>
          </div>
        </div>
      </div>

      {/* Details Section */}
      <div style={styles.detailsContainer}>
        <h2 style={styles.sectionHeading}>Detailed Information</h2>

        <div style={styles.detailsList}>
          {Object.entries(data)
            .filter(
              ([key]) =>
                !hiddenFields.includes(key) &&
                !["company_name", "actionable_number"].includes(key)
            )
            .map(([key, value], i) => {
              const isStatus = key.toLowerCase() === "status";
              return (
                <div
                  key={key}
                  style={{
                    ...styles.detailRow,
                    ...(i % 2 === 0 ? styles.rowEven : styles.rowOdd),
                    ...(isStatus ? getStatusStyle(value) : {}),
                  }}
                >
                  <div style={styles.detailKey}>
                    {key.replace(/_/g, " ").toUpperCase()}
                  </div>
                  <div style={styles.detailValue}>{value || "—"}</div>
                </div>
              );
            })}
        </div>
      </div>

      {/* Upload Section — ALWAYS visible for External Trigger */}
      <div style={styles.uploadContainer}>
        <h2 style={styles.uploadHeading}>Upload Evidence</h2>

        <div style={styles.uploadBox}>
          <input
            type="file"
            style={styles.fileInput}
            onChange={(e) => setFile(e.target.files[0])}
          />
          <button style={styles.uploadBtn} onClick={handleSubmitEvidence}>
            ⬆ Submit Evidence
          </button>
        </div>
      </div>
    </div>
  );
};

/* Reusing the same CSS structure from ActionableDetail.jsx */
const styles = {
  page: {
    padding: "40px 50px",
    background: "linear-gradient(135deg, #f6f9fc, #e9f0fa)",
    minHeight: "100vh",
    fontFamily: "Segoe UI, Roboto, sans-serif",
    color: "#333",
  },
  header: {
    borderBottom: "4px solid #007bff",
    paddingBottom: "15px",
    marginBottom: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  backBtn: {
    background: "none",
    border: "none",
    color: "#007bff",
    fontSize: "15px",
    cursor: "pointer",
    fontWeight: "500",
  },
  title: {
    fontSize: "28px",
    fontWeight: "700",
    color: "#1e1e1e",
  },
  summaryContainer: {
    display: "flex",
    justifyContent: "space-around",
    alignItems: "center",
    background: "rgba(255, 255, 255, 0.7)",
    backdropFilter: "blur(10px)",
    borderRadius: "14px",
    padding: "20px",
    boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
    marginBottom: "35px",
    flexWrap: "wrap",
    gap: "15px",
  },
  summaryItem: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: "240px",
  },
  emoji: {
    fontSize: "22px",
  },
  label: {
    fontSize: "13px",
    color: "#777",
    textTransform: "uppercase",
  },
  value: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#111",
  },
  detailsContainer: {
    background: "#ffffff",
    borderRadius: "14px",
    boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
    overflow: "hidden",
  },
  sectionHeading: {
    fontSize: "20px",
    fontWeight: "600",
    background: "#f1f5fb",
    padding: "15px 20px",
    borderBottom: "1px solid #e1e6ee",
  },
  detailsList: {
    display: "flex",
    flexDirection: "column",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "14px 20px",
    alignItems: "center",
  },
  rowEven: {
    background: "#fafbfc",
  },
  rowOdd: {
    background: "#f3f6fa",
  },
  detailKey: {
    fontWeight: "600",
    fontSize: "13px",
    textTransform: "uppercase",
    color: "#555",
    flex: "1",
  },
  detailValue: {
    flex: "2",
    textAlign: "right",
    fontSize: "15px",
    fontWeight: "500",
    color: "#222",
    wordWrap: "break-word",
  },
  uploadContainer: {
    marginTop: "40px",
    background: "#f8fbff",
    border: "2px dashed #a7c8ff",
    borderRadius: "14px",
    padding: "30px",
    textAlign: "center",
    boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
  },
  uploadHeading: {
    fontSize: "20px",
    fontWeight: "600",
    color: "#004aad",
  },
  uploadText: {
    fontSize: "14px",
    color: "#666",
    marginBottom: "15px",
  },
  uploadBox: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    flexWrap: "wrap",
  },
  fileInput: {
    padding: "8px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    background: "#fff",
  },
  uploadBtn: {
    backgroundColor: "#007bff",
    color: "#fff",
    border: "none",
    padding: "10px 18px",
    fontSize: "15px",
    borderRadius: "6px",
    cursor: "pointer",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "100vh",
  },
  loadingText: {
    color: "#666",
    fontSize: "18px",
    fontWeight: "500",
  },
};

export default ActionableMasterDetail;
